import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import ThumbnailActions from "Redux/ThumbnailRedux";
import Spinner from "./Spinner";
import useEscapeKey from "../hooks/useEscapeKey";

export default function ImageHistoryModal({
  isOpen,
  onClose,
  segment,
  onSelectImage,
  isSelecting = false,
}) {
  useEscapeKey(isOpen, onClose);
  const dispatch = useDispatch();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);

  const loadHistory = useCallback(() => {
    if (!segment?.id) return;
    
    setIsLoading(true);
    setError("");
    dispatch(ThumbnailActions.thumbnailHistoryRequest(segment.id, (data, loadError) => {
      setIsLoading(false);
      if (loadError) {
        setError(loadError || "Failed to load image history");
        return;
      }
      setHistory(data.history || []);
    }));
  }, [dispatch, segment?.id]);

  useEffect(() => {
    if (isOpen && segment?.id) {
      loadHistory();
    }
  }, [isOpen, segment?.id, loadHistory]);

  if (!isOpen || !segment) return null;

  const handleSelect = (index) => {
    if (history[index]?.current) return;
    setSelectedIndex(index);
    onSelectImage(segment.id, index);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-2xl sm:mx-4 max-h-[90vh] sm:max-h-[80vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Image History</h3>
            <button
              onClick={onClose}
              disabled={isSelecting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Click on a previous image to restore it
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Spinner size="lg" />
              <span className="text-sm text-gray-400">Loading history…</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-4xl mb-2">📷</p>
              <p>No image history available</p>
              <p className="text-sm text-gray-400 mt-1">
                Regenerate the image to create history
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {history.map((item, index) => (
                <div
                  key={index}
                  onClick={() => !item.current && !isSelecting && handleSelect(index)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                    item.current
                      ? "border-primary-500 ring-2 ring-primary-200"
                      : isSelecting && selectedIndex === index
                      ? "border-primary-400 opacity-75"
                      : "border-gray-200 hover:border-gray-300 cursor-pointer"
                  }`}
                >
                  <div className="aspect-video bg-gray-100">
                    <img
                      src={item.image_url}
                      alt={`Version ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-2 bg-white">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">
                        Version {index + 1}
                      </span>
                      {item.current && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate" title={item.prompt}>
                      {item.prompt || "No prompt"}
                    </p>
                    <p className="text-xs text-gray-300 mt-1">
                      {formatDate(item.created_at)}
                    </p>
                  </div>

                  {isSelecting && selectedIndex === index && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <Spinner size="md" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isSelecting}
            className="w-full px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
